import type { RefOrGetter } from "ls/common/types";
import type { DeepResult } from "./types";
import type { DeepSearchDataJs } from "@/generated/models";
import { useQuery } from "@tanstack/vue-query";
import { refDebounced } from "@vueuse/core";
import { computed, ref, toValue, watch } from "vue";
import { queries } from "./queries";

export interface UseDeepSearchOptions {
  limit?: RefOrGetter<number>;
  debounceMs?: RefOrGetter<number>;
  enabled?: RefOrGetter<boolean>;
};

export function useDeepSearch(
  searchTerm: RefOrGetter<string>,
  options: UseDeepSearchOptions = {},
) {
  const debouncedTerm = refDebounced(computed(() => toValue(searchTerm).trim()), options.debounceMs);
  const deepOpts = computed(() => ({
    ...queries.deepSearch(
      { searchQuery: debouncedTerm.value, limit: toValue(options.limit) },
      (toValue(options.enabled) ?? true) && debouncedTerm.value.length > 0,
    ),
    keepPreviousData: true,
  }));

  const { data, isPending, isLoading, isFetching, isSuccess } = useQuery(deepOpts);

  const previous = ref<DeepSearchDataJs | undefined>(undefined);
  watch(
    () => data.value,
    newData => {
      if (newData !== undefined) previous.value = newData;
    },
  );

  const rawResults = computed(() => {
    if (!debouncedTerm.value && previous.value === undefined) {
      return undefined;
    }
    return data.value ?? previous.value;
  },
  );

  const workspaces = computed(() => rawResults.value ? rawResults.value.WorkspaceNameResults.map(w => ({ ...w, kind: "workspace" } satisfies DeepResult)) ?? [] : []);
  const folders = computed(() => rawResults.value ? rawResults.value.FolderNameResults.map(f => ({ ...f, kind: "folder" } satisfies DeepResult)) ?? [] : []);
  const projects = computed(() => rawResults.value ? rawResults.value.ProjectNameResults.map(p => ({ ...p, kind: "project" } satisfies DeepResult)) ?? [] : []);

  const pages = computed(() => rawResults.value ? rawResults.value.PageNameResults.map(p => ({ ...p, kind: "page" } satisfies DeepResult)) ?? [] : []);
  const rpDeep = computed(() => rawResults.value ? rawResults.value.RpResults.map(r => ({ ...r, kind: "rp" } satisfies DeepResult)) ?? [] : []);
  const canvasDeep = computed(() => rawResults.value ? rawResults.value.CanvasResults.map(c => ({ ...c, kind: "canvas" } satisfies DeepResult)) ?? [] : []);
  const docsDeep = computed(() => rawResults.value ? rawResults.value.DocumentResults.map(d => ({ ...d, kind: "doc" } satisfies DeepResult)) ?? [] : []);
  const commentsDeep = computed(() => rawResults.value ? rawResults.value.CommentResults.map(c => ({ ...c, kind: "comment" } satisfies DeepResult)) ?? [] : []);
  const notesDeep = computed(() => rawResults.value ? rawResults.value.NotesResults.map(n => ({ ...n, kind: "note" } satisfies DeepResult)) ?? [] : []);

  return {
    workspaces,
    folders,
    projects,
    pages,
    rpDeep,
    canvasDeep,
    docsDeep,
    commentsDeep,
    notesDeep,
    isPending,
    isLoading,
    isFetching,
    isSuccess,
  };
}
